import { useDeliveryZones, useOrderTypeConfig } from './useOrderSetup';
import type { DeliveryLeg } from '../components/LeadTimeDiagram';

/**
 * The delivery leg to draw under a channel's timing preview.
 *
 * Only DELIVERY has one, and only the zone shapes that carry a static time can
 * be previewed: ROUTE mode resolves the drive per address at checkout, so there
 * is no single number to show — saying "30 min" there would be a guess dressed
 * up as a setting.
 */
export function useDeliveryLeg(orderType: 'PICKUP' | 'DELIVERY'): DeliveryLeg {
  const { data: configs } = useOrderTypeConfig();
  const { data: zones } = useDeliveryZones();
  if (orderType !== 'DELIVERY') return null;

  const cfg = configs?.find((c) => c.orderType === 'DELIVERY');
  const priceMode = cfg?.deliveryPriceMode ?? 'DISTANCE';
  const distanceCalc = cfg?.distanceCalculation ?? 'DIRECT';
  if (priceMode === 'DISTANCE' && distanceCalc === 'ROUTE') return { kind: 'live' };

  // Only zones of the shape this pricing mode actually consults — ones left
  // behind by a mode switch are inert and would skew the range.
  const wantMode = priceMode === 'ZONE' ? 'POLYGON' : 'STRAIGHT_LINE';
  const minutes = (zones ?? [])
    .filter((z) => z.isActive && z.mode === wantMode && z.deliveryMinutes != null)
    .map((z) => z.deliveryMinutes as number);
  if (minutes.length === 0) return null;

  return { kind: 'static', minMinutes: Math.min(...minutes), maxMinutes: Math.max(...minutes) };
}
